import PropTypes from 'prop-types';
import { buildUseStyles, useTheme } from 'Components/ComponentLibrary/ExternalLibraries/Libraries/MaterialV4/styles';
import React from 'react';
import classNames from 'classnames';
import Typography from 'Components/ComponentLibrary/ExternalLibraries/Libraries/MaterialV4/Typography';
import MetaControl from 'Components/UI/Meta/MetaControl';
import { useMessagesContext } from 'Views/Page/Content/Templates/MessagesPage/MessagesContext';
import {
  ComposeMessageRecipientsOverrides,
} from 'Views/Page/Content/Templates/MessagesPage/components/MessagesPageContent/components/ComposeMessage/components/ComposeMessageRecipients/ComposeMessageRecipientsOverrides';

const useStyles = buildUseStyles('ComposeMessageRecipients', (theme) => ({
  root: {},
  participantInputWrapper: {
    display: 'flex',
    flexDirection: 'row',
  },
  participantInput: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'stretch',
  },
  inputCol: {
    display: 'flex',
    flexDirection: 'column',
    flexGrow: 1,
  },
  emailInputAutoComplete: {
    flexGrow: 1,
    padding: 0,
    paddingBottom: 0,
  },
  emailParticipantTypeLabel: {
    margin: theme.spacing(1, 1, 1, 0),
    fontWeight: theme.typography.fontWeightBold,
  },
}));

const ComposeMessageRecipients = ({
  className,
}) => {
  const theme = useTheme();
  const classes = useStyles(theme);
  const {
    composeDraftMessageResource,
    autoSaveComposeDraft,
    setComposeDraftMessageResource,
  } = useMessagesContext();

  const getInternalRecipientId = (selected) => {
    const options = composeDraftMessageResource?.meta().field('messageRecipients').options().list();
    let returnValue = null;
    options?.forEach((option) => {
      if (option.attr('prompt') === selected || option.attr('id') === selected) {
        returnValue = option.attr('id');
      }
    });
    return returnValue;
  };

  const internalRecipientAlreadyExists = (selected) => composeDraftMessageResource?.attr('messageRecipients')?.includes(selected);
  const externalRecipientAlreadyExists = (selected) => composeDraftMessageResource?.attr('externalMessageRecipients')?.includes(selected);

  return (
    <div className={classNames(classes.root, className)}>
      <div className={classes.participantInputWrapper}>
        <div className={classes.inputCol}>
          <div className={classes.participantInput} id="participant-input">
            <Typography
              variant="h5"
              color="primaryGray"
              component="span"
              className={classes.emailParticipantTypeLabel}
            >
              {'To:'}
            </Typography>
            <MetaControl
              className={classes.emailInputAutoComplete}
              metaOverrideSet={ComposeMessageRecipientsOverrides}
              resource={composeDraftMessageResource}
              attrName="messageRecipients"
              onChange={(value) => {
                const externalMessageRecipients = [];
                const internalMessageRecipients = [];
                value?.forEach((recipient) => {
                  if (getInternalRecipientId(recipient)) {
                    internalMessageRecipients.push(recipient);
                    return;
                  }
                  externalMessageRecipients.push(recipient);
                });

                composeDraftMessageResource.attr('externalMessageRecipients', externalMessageRecipients);
                composeDraftMessageResource.attr('messageRecipients', internalMessageRecipients);
                setComposeDraftMessageResource(composeDraftMessageResource);
                autoSaveComposeDraft(composeDraftMessageResource);
              }}
              onKeyDown={async (event) => {
                if (event.key === 'Tab') {
                  const internalRecipientId = getInternalRecipientId(event.target.value);

                  // handle internal recipients
                  if (internalRecipientId) {
                    const internalMessageRecipients = composeDraftMessageResource.attr('messageRecipients');
                    if (!internalRecipientAlreadyExists(internalRecipientId)) {
                      internalMessageRecipients.push(internalRecipientId);
                    }
                    composeDraftMessageResource.attr('internalMessageRecipients', internalMessageRecipients);
                    setComposeDraftMessageResource(composeDraftMessageResource);
                    autoSaveComposeDraft(composeDraftMessageResource);
                    return;
                  }

                  // handle external recipients
                  const externalMessageRecipients = composeDraftMessageResource.attr('externalMessageRecipients');
                  if (!externalRecipientAlreadyExists(event.target.value)) {
                    externalMessageRecipients.push(event.target.value);
                  }
                  composeDraftMessageResource.attr('externalMessageRecipients', externalMessageRecipients);
                  setComposeDraftMessageResource(composeDraftMessageResource);
                  autoSaveComposeDraft(composeDraftMessageResource);
                }
              }}
              value={composeDraftMessageResource?.attr('messageRecipients')?.concat(composeDraftMessageResource?.attr('externalMessageRecipients'))}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

ComposeMessageRecipients.defaultProps = {
  className: '',
};

ComposeMessageRecipients.propTypes = {
  className: PropTypes.string,
};

export { ComposeMessageRecipients };
